import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { bk, nok, errMsg, custToken } from "@/lib/booking";
import { useSeo, track } from "@/lib/seo";

const PREMISES = [["bolig", "Bolig"], ["kontor", "Kontor"], ["sameie", "Sameie / fellesareal"], ["annet", "Annet lokale"]];
const FREQ = [["ukentlig", "Hver uke"], ["annenhver_uke", "Annenhver uke"], ["hver_4_uke", "Hver 4. uke (månedlig)"], ["annet", "Annen frekvens"]];
const ROOMS = { bolig: ["Kjøkken", "Bad", "Stue", "Soverom", "Gang", "Vaskerom"], kontor: ["Arbeidsplasser", "Kjøkken/pauserom", "Toaletter", "Møterom", "Resepsjon"], sameie: ["Trapperom", "Inngangsparti", "Heis", "Vaskerom", "Boder/kjeller"], annet: ["Hovedrom", "Toaletter", "Kjøkken", "Gang"] };
const DAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"];
const chip = (on) => `rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${on ? "border-[#2F5D50] bg-[#2F5D50] text-white" : "border-stone-200 bg-white hover:border-[#2F5D50]/50"}`;
const Box = ({ title, children }) => <section className="rounded-2xl border border-stone-200 bg-white p-5"><h2 className="font-heading font-bold mb-3">{title}</h2>{children}</section>;

export default function FastAgreement() {
  useSeo({ title: "Fast renholdsavtale – hjem, kontor og sameie | Ask-Stokke Renhold", path: "/fast-renholdsavtale",
    description: "Be om fast renhold hver uke, annenhver uke eller månedlig. Få veiledende pris med en gang – ingenting er bindende før du har godtatt avtalen." });
  const [cat, setCat] = useState(null);
  const [f, setF] = useState({ premises: "bolig", address: "", postcode: "", sqm: 80, rooms: [], frequency: "annenhver_uke", frequency_note: "", weekday: 0, time: "09:00",
    addons: {}, products: "vi_tar_med", allergies: "", instructions: "", name: "", email: "", phone: "" });
  const [est, setEst] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    bk.catalog().then(setCat);
    if (custToken()) bk.me().then((m) => setF((p) => ({ ...p, name: m.name, email: m.email, phone: m.phone || "" }))).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => bk.estimate({ premises: f.premises, sqm: Number(f.sqm) || 0, frequency: f.frequency, addons: f.addons }).then(setEst).catch(() => setEst(null)), 300);
    return () => clearTimeout(t);
  }, [f.premises, f.sqm, f.frequency, f.addons]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await bk.requestAgreement({ ...f, sqm: Number(f.sqm), weekday: Number(f.weekday) });
      track("agreement_requested", { premises: f.premises, frequency: f.frequency });
      setDone(r);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (done) return (
    <main className="mx-auto max-w-xl px-5 py-16 text-center" data-testid="agreement-success">
      <CheckCircle2 className="mx-auto h-14 w-14 text-[#2F5D50]" />
      <h1 className="mt-4 font-heading text-3xl font-extrabold">Forespørselen er mottatt</h1>
      <p className="mt-2 text-stone-600">Referanse <b data-testid="agreement-ref">{done.ref}</b>. Dette er ikke en bindende avtale – vi kontakter deg for å avklare detaljer og endelig pris.</p>
      <p className="mt-2 text-sm text-stone-500">{done.email_sent ? "Du har fått en bekreftelse på e-post." : "Vi kunne ikke sende e-postbekreftelse akkurat nå, men forespørselen er lagret."}</p>
      <Link to="/" className="mt-6 inline-block rounded-full bg-[#2F5D50] px-6 py-3 font-bold text-white">Til forsiden</Link>
    </main>
  );
  const addons = (cat?.addons || []).filter((a) => ["vinduer", "kjoleskap", "ovn", "sengeskift"].includes(a.key));
  const ready = f.address && /^\d{4}$/.test(f.postcode) && f.name && f.email && f.phone.length >= 8 && Number(f.sqm) > 0;

  return (
    <main className="mx-auto max-w-6xl px-5 py-10 grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="agreement-page">
      <div className="space-y-4 min-w-0">
        <div>
          <span className="rounded-full bg-[#E7EFE9] px-3 py-1 text-xs font-bold text-[#2F5D50]">Fast avtale – gjentakende renhold</span>
          <h1 className="mt-3 font-heading text-4xl font-extrabold text-stone-900">Få fast renholdsavtale</h1>
          <p className="mt-2 text-stone-600">Skal du bare ha én vask? <Link to="/bestill" className="underline">Bestill engangsrenhold her</Link>.</p>
        </div>
        <Box title="1. Hva skal rengjøres?">
          <div className="flex flex-wrap gap-2">{PREMISES.map(([k, l]) => <button key={k} data-testid={`premises-${k}`} className={chip(f.premises === k)} onClick={() => { set("premises", k); set("rooms", []); }}>{l}</button>)}</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_120px_120px]">
            <Input aria-label="Adresse" data-testid="agr-address" placeholder="Adresse" value={f.address} onChange={(e) => set("address", e.target.value)} />
            <Input aria-label="Postnummer" data-testid="agr-postcode" placeholder="Postnr" maxLength={4} value={f.postcode} onChange={(e) => set("postcode", e.target.value.replace(/\D/g, ""))} />
            <Input aria-label="Kvadratmeter" data-testid="agr-sqm" type="number" placeholder="m²" value={f.sqm} onChange={(e) => set("sqm", e.target.value)} />
          </div>
          <div className="mt-4 text-sm font-semibold">Rom og områder</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {ROOMS[f.premises].map((r) => <button key={r} className={chip(f.rooms.includes(r))} onClick={() => set("rooms", f.rooms.includes(r) ? f.rooms.filter((x) => x !== r) : [...f.rooms, r])}>{r}</button>)}
          </div>
        </Box>
        <Box title="2. Hvor ofte og når?">
          <div className="flex flex-wrap gap-2">{FREQ.map(([k, l]) => <button key={k} data-testid={`freq-${k}`} className={chip(f.frequency === k)} onClick={() => set("frequency", k)}>{l}</button>)}</div>
          {f.frequency === "annet" && <Input className="mt-3" placeholder="Beskriv ønsket frekvens" value={f.frequency_note} onChange={(e) => set("frequency_note", e.target.value)} />}
          <div className="mt-4 flex flex-wrap gap-3">
            <select aria-label="Ukedag" data-testid="agr-weekday" className="h-10 rounded-md border border-stone-200 bg-white px-3 text-sm" value={f.weekday} onChange={(e) => set("weekday", e.target.value)}>
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <Input aria-label="Tidspunkt" data-testid="agr-time" type="time" className="w-32" value={f.time} onChange={(e) => set("time", e.target.value)} />
          </div>
        </Box>
        <Box title="3. Tillegg, produkter og ønsker">
          <div className="grid gap-2 sm:grid-cols-2">
            {addons.map((a) => (
              <label key={a.key} className="flex items-center gap-2 rounded-xl border border-stone-200 px-3 py-2 text-sm">
                <Checkbox checked={!!f.addons[a.key]} onCheckedChange={(v) => set("addons", { ...f.addons, [a.key]: v ? 1 : 0 })} />
                <span className="flex-1">{a.name}</span><span className="text-stone-500">{nok(a.price)}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="agr-products-ours" className={chip(f.products === "vi_tar_med")} onClick={() => set("products", "vi_tar_med")}>Dere tar med produkter</button>
            <button data-testid="agr-products-own" className={chip(f.products === "kundens")} onClick={() => set("products", "kundens")}>Bruk våre egne produkter</button>
          </div>
          <Input className="mt-3" placeholder="Allergier eller produktønsker" value={f.allergies} onChange={(e) => set("allergies", e.target.value)} />
          <Textarea className="mt-3" data-testid="agr-instructions" placeholder="Spesielle ønsker og instruksjoner" value={f.instructions} onChange={(e) => set("instructions", e.target.value)} />
        </Box>
        <Box title="4. Kontaktinformasjon">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input aria-label="Navn" data-testid="agr-name" placeholder="Navn / firma" value={f.name} onChange={(e) => set("name", e.target.value)} />
            <Input aria-label="E-post" data-testid="agr-email" type="email" placeholder="E-post" value={f.email} onChange={(e) => set("email", e.target.value)} />
            <Input aria-label="Telefon" data-testid="agr-phone" placeholder="Telefon" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
        </Box>
      </div>
      <aside className="lg:sticky lg:top-20 h-fit rounded-2xl border border-stone-200 bg-white p-5" data-testid="agreement-estimate">
        <h2 className="font-heading font-bold">Veiledende pris</h2>
        {est ? (
          <>
            <div className="mt-2 font-heading text-3xl font-extrabold text-[#2F5D50]" data-testid="agr-estimate-value">{nok(est.total_per_visit)}</div>
            <div className="text-sm text-stone-600">per besøk · ca. {est.duration_hours} timer{est.vat_registered ? " · inkl. mva" : ""}</div>
          </>
        ) : <p className="mt-2 text-sm text-stone-500">Fyll inn størrelse.</p>}
        <p className="mt-3 text-xs text-stone-500">Endelig pris avtales etter vurdering eller befaring. Forespørselen er ikke bindende, og ingen betaling trekkes.</p>
        <button data-testid="agr-submit" disabled={!ready || busy} onClick={submit} className="mt-4 w-full rounded-full bg-[#2F5D50] py-3.5 font-bold text-white disabled:opacity-40">
          {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Send forespørsel"}
        </button>
      </aside>
    </main>
  );
}
